import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styles from './PairedSampleList.module.css';
import upTriangleRed from '../../img/icon/upTriangleRed.png';

function PairedSampleList({ sampleName, onClose }) {
    const { t } = useTranslation();
    const [pairedData, setPairedData] = useState([]);
    const [correlationData, setCorrelationData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ★ 소수점 자리수를 강제로 맞추는 유틸리티 함수 (빈자리 0 채움)
    const formatNumber = (value, decimals) => {
        if (value === null || value === undefined || value === '') return value;
        const num = Number(value);
        return isNaN(num) ? value : num.toFixed(decimals);
    };

    useEffect(() => {
        const fetchPaired = async () => {
            try {
                const res = await axios.get('/api/getWESPairedSummary', {
                    params: { sampleName }
                });
                const transformed = res.data.map(item => ({
                    pairedSample: item.sampleName === sampleName
                        ? item.pairedSampleName
                        : item.sampleName,
                    mutationCount: item.mutationCount,
                    // ★ Tumor Purity와 Ploidy는 소수점 6자리 고정
                    tumorPurity: formatNumber(item.tumorPurity, 6),
                    ploidy: formatNumber(item.ploidy, 6),
                    status: item.status,
                    // ★ MSI(%)는 소수점 2자리 고정
                    msiPercent: formatNumber(item.msiPercent, 2)
                }));
                setPairedData(transformed);
            } catch (err) {
                console.error(err);
                setError(t('데이터를 불러오는 중 오류가 발생했습니다.'));
            } finally {
                setLoading(false);
            }
        };
        fetchPaired();

        const fetchCorellation = async () => {
            try {
                const res = await axios.get('/api/getWESCorrelationSummary', {
                    params: { sampleName }
                });

                const transformed = res.data.map(item => {
                    const { primaryTumor, secondaryTumor, matchedNormal } = item;

                    let matchedSample1 = null;
                    let matchedSample2 = null;

                    // sampleName 이 어떤 컬럼인지에 따라 나머지 둘을 matchedSample1/2 로 세팅
                    if (sampleName === primaryTumor) {
                        matchedSample1 = secondaryTumor;
                        matchedSample2 = matchedNormal;
                    } else if (sampleName === secondaryTumor) {
                        matchedSample1 = primaryTumor;
                        matchedSample2 = matchedNormal;
                    } else if (sampleName === matchedNormal) {
                        matchedSample1 = primaryTumor;
                        matchedSample2 = secondaryTumor;
                    }

                    return {
                        matchedSample1,
                        matchedSample2,
                        tumorUniqueCount: item.tumorUniqueCount,
                        pairUniqueCount: item.pairUniqueCount,
                        sharedCount: item.sharedCount,
                        sharedRatioInTumor: item.sharedRatioInTumor,
                        sharedRatioInPair: item.sharedRatioInPair,
                        afCorrelation: item.vafCorrelation,
                        // ★ variantOverlapPercent(Shared %)는 소수점 2자리 고정
                        variantOverlapPercent: formatNumber(item.variantOverlapPercent, 2),
                    };
                });

                setCorrelationData(transformed);
            } catch (err) {
                console.error(err);
                setError(t('데이터를 불러오는 중 오류가 발생했습니다.'));
            } finally {
                setLoading(false);
            }
        }
        fetchCorellation();
    }, [sampleName, t]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.sampleName}>{sampleName}</div>
                <button
                    type="button"
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="닫기"
                    title="닫기"
                />
            </div>
            <div className={styles.content}>
                {loading && <p>{t('로딩 중...')}</p>}
                {error && <p className={styles.error}>{error}</p>}
                {!loading && !error && pairedData.length > 0 && (
                    <div>
                        <div>Paired Summary</div>
                        <div className={styles.divTable} role="table" aria-label="Paired samples">
                            <div className={`${styles.divRow} ${styles.divHeader}`} role="row">
                                <div className={styles.divCell} role="columnheader">Paired Sample</div>
                                <div className={styles.divCell} role="columnheader">Mutation Count (TO)</div>
                                <div className={styles.divCell} role="columnheader">Tumor Purity</div>
                                <div className={styles.divCell} role="columnheader">Ploidy</div>
                                <div className={styles.divCell} role="columnheader">Status</div>
                                <div className={styles.divCell} role="columnheader">MSI (%)</div>
                            </div>
                            {/* 밑줄 전용 “행 + 셀” */}
                            <div className={styles.underLineRow} role="row" aria-hidden="true">
                                <div className={styles.underLineCell} role="cell" />
                                <div className={styles.underLineCell} role="cell" />
                                <div className={styles.underLineCell} role="cell" />
                                <div className={styles.underLineCell} role="cell" />
                                <div className={styles.underLineCell} role="cell" />
                                <div className={styles.underLineCell} role="cell" />
                            </div>

                            {pairedData.map((row, idx) => (
                                <div className={styles.divRow} role="row" key={idx}>
                                    <div className={styles.divCellContent} role="cell">{row.pairedSample}</div>
                                    <div className={styles.divCellContent} role="cell">{row.mutationCount}</div>
                                    <div className={styles.divCellContent} role="cell">{row.tumorPurity}</div>
                                    <div className={styles.divCellContent} role="cell">{row.ploidy}</div>
                                    <div className={styles.divCellContent} role="cell">{row.status}</div>
                                    <div className={styles.divCellContent} role="cell">
                                        {row.msiPercent}
                                        {Number(row.msiPercent) >= 10 && (
                                            <img
                                                src={upTriangleRed}
                                                alt="MSI high"
                                                className={styles.msiIcon} 
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className={styles.blankOneLine}></div>
                        <div>
                            <div>Correlation</div>
                        </div>
                        <div className={styles.divTable} role="table" aria-label="Paired samples">
                            <div className={`${styles.divRow} ${styles.divHeader}`} role="row">
                                <div className={styles.divCellCorr} role="columnheader">Tissue Sample</div>
                                <div className={styles.divCellCorr} role="columnheader">Tissue Mutation (T)</div>
                                <div className={styles.divCellCorr} role="columnheader">Shared</div>
                                <div className={styles.divCellCorr} role="columnheader">Shared/(TO+TT) (%)</div>
                            </div>

                            {/* 밑줄 전용 행 */}
                            <div className={styles.underLineRow} role="row" aria-hidden="true">
                                <div className={styles.underLineCell} role="cell" />
                                <div className={styles.underLineCell} role="cell" />
                                <div className={styles.underLineCell} role="cell" />
                                <div className={styles.underLineCell} role="cell" />
                            </div>

                            {correlationData.map((row, idx) => (
                                <div className={styles.divRow} role="row" key={idx}>
                                    <div className={styles.divCellContent} role="cell">
                                        {row.matchedSample1}_{row.matchedSample2}
                                    </div>
                                    <div className={styles.divCellContent} role="cell">
                                        {row.pairUniqueCount}
                                    </div>
                                    <div className={styles.divCellContent} role="cell">
                                        {row.sharedCount}
                                    </div>
                                    <div className={styles.divCellContent} role="cell">
                                        {row.variantOverlapPercent}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {!loading && !error && pairedData.length === 0 && (
                    <p>{t('페어드 샘플 데이터가 없습니다.')}</p>
                )}
            </div>
        </div>
    );
}

export default PairedSampleList;